import React, { useState, useEffect, useMemo } from "react";
import { CircularProgress, Box } from "@mui/material";
import Swal from "sweetalert2";

import {
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../data/Axios/DynamicService";

import { HRService, caseManagementUrl } from "../../../config";

import DynamicForm from "../../../data/Axios/DynamicForm";
import { imprestApplicationsForms } from "../../../data/DynamicTable/FormsFields/LeaveApplicationForms";
import { allCasesView } from "../../../data/Axios/queries";
import { AllEmployeesQuery } from "../../../data/Queries/HRmanagement/leavemanagement";
import { executeAnyMutation } from "../../../data/DynamicServiceNew/jsondynamicservice";
import { uploadDocuments } from "../../../data/DocumentManager/documentManagerClient";

/* ---------------------------------------------------------------------
   REST FILE UPLOAD FUNCTION (MATCHES STAFF CLAIM STRUCTURE)
------------------------------------------------------------------------ */
const uploadSupportingDocuments = async ({
  imprestId,
  employeeId,
  files,
}) => {
  console.log("Uploading imprest documents...", {
    imprestId,
    employeeId,
    files,
  });

  return uploadDocuments(
    "/upload/imprest",
    {
      imprest_id: imprestId,
      employee_id: employeeId,
    },
    files
  );
};

/* ---------------------------------------------------------------------
   IMPREST APPLICATION FORM
------------------------------------------------------------------------ */
const ImprestApplicationForm = ({ data, isEditing, onClose, onAction }) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");

  const userIdFromToken = Number(decodedToken.Id);
  const EmployeeIdFromToken = Number(decodedToken.EmployeeId);
  const companyId = Number(decodedToken.CompanyId);
  const rolegroupId = parseInt(decodedToken.RoleGroupId);

  const [employees, setEmployees] = useState([]);
  const [cases, setCases] = useState([]);
  const [showCases, setShowCases] = useState(data?.caseRelated === 1);
  const [showEmployeeDropDown, setShowEmployeeDropDown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [supportingDocuments, setSupportingDocuments] = useState([]);

  /* -----------------------------------------------------------------
     LOAD STATIC DATA
  ----------------------------------------------------------------- */
  useEffect(() => {
    const loadData = async () => {
      try {
        const [casesData, employeeData] = await Promise.all([
          graphqlQuery(allCasesView, caseManagementUrl.uri),
          graphqlQuery(AllEmployeesQuery, HRService.uri),
        ]);

        setCases(casesData || []);
        setEmployees(employeeData || []);

        if (rolegroupId === 1 || rolegroupId === 2) {
          setShowEmployeeDropDown(true);
        }
      } catch (err) {
        console.error("Error loading static data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  /* -----------------------------------------------------------------
     OPTIONS
  ----------------------------------------------------------------- */
  const employeeOptions = useMemo(
    () =>
      employees.map((e) => ({
        value: e.id,
        label: `${e.firstName} ${e.lastName}`,
      })),
    [employees],
  );

  const caseOptions = useMemo(
    () =>
      cases.map((c) => ({
        value: c.id,
        label: c.title,
      })),
    [cases],
  );

  /* -----------------------------------------------------------------
     FORM FIELDS
  ----------------------------------------------------------------- */
  const fields = useMemo(() => {
    const base = [
      ...(showEmployeeDropDown
        ? [
          {
            name: "applicationMode",
            label: "Apply For",
            type: "select",
            options: [
              { value: "self", label: "Apply for Self" },
              { value: "team", label: "Apply for Team Member" },
            ],
            required: true,
            gridColumn: "span 2",
          },
          {
            name: "employeeId",
            label: "Employee",
            type: "select",
            options: employeeOptions,
            required: true,
            gridColumn: "span 2",
            visibleCondition: (values) => values.applicationMode === "team",
          },
        ]
        : []),

      ...imprestApplicationsForms,

      {
        name: "supportingDocuments",
        label: "Upload the supporting documents",
        type: "file",
        inputProps: {
          multiple: true,
          accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png",
          onChange: (e) => {
            const files = Array.from(e.target.files);
            const maxSize = 5 * 1024 * 1024; // 5 MB
            const oversized = files.filter((f) => f.size > maxSize);
            
            if (oversized.length > 0) {
              Swal.fire({
                icon: "error",
                title: "File too large",
                text: `Each document must be 5MB or smaller. ${oversized.length} file(s) exceeded the limit.`,
              });
              setSupportingDocuments([]);
              e.target.value = ""; // Reset input
            } else {
              console.log("Files Uploaded:", files);
              setSupportingDocuments(files);
            }
          },
        },
        gridColumn: "span 2",
      },
    ];

    if (showCases) {
      base.push({
        name: "caseId",
        label: "Case",
        type: "select",
        options: caseOptions,
        gridColumn: "span 2",
      });
    }

    return base;
  }, [employeeOptions, caseOptions, showCases, showEmployeeDropDown]);

  /* -----------------------------------------------------------------
     INITIAL VALUES
  ----------------------------------------------------------------- */
  const initialValues = {
    applicationMode: "self",
    employeeId: data?.employeeId ?? EmployeeIdFromToken,
    description: data?.description || "",
    caseRelated: data?.caseRelated || false,
    caseId: data?.caseId || "",
    supportingDocuments: [],
  };

  /* -----------------------------------------------------------------
     STEPS
  ----------------------------------------------------------------- */
  const steps = useMemo(() => {
    const stepFields = [
      ...(showEmployeeDropDown ? ["applicationMode", "employeeId"] : []),
      "description",
      "caseRelated",
      "supportingDocuments",
    ];

    if (showCases) stepFields.push("caseId");

    return [
      {
        stepLabel: "Imprest Information",
        stepDescription:
          "Enter imprest details and upload supporting documents",
        icon: "payment",
        fields: stepFields,
      },
    ];
  }, [showCases, showEmployeeDropDown]);

  /* -----------------------------------------------------------------
     HANDLE SUBMIT
  ----------------------------------------------------------------- */
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // 1️⃣ Resolve userId based on role and applicationMode
      const isApplyingForTeam =
        (rolegroupId === 1 || rolegroupId === 2) &&
        values.applicationMode === "team";
      const resolvedEmployeeId = isApplyingForTeam
        ? values.employeeId
        : EmployeeIdFromToken;

      // 2️⃣ Prepare GraphQL payload
      const payload = {
        employeeId: resolvedEmployeeId,
        companyId,
        createdBy: userIdFromToken,
        description: values.description,
        caseRelated: values.caseRelated,
        caseId: values.caseId || null,
      };

      if (isEditing) payload.id = data.id;

      console.log("Submitting imprest payload:", payload);

      // 2️⃣ Create / update imprest
      const response = await executeAnyMutation(
        isEditing ? "updateImprestApplication" : "createImprestApplication",
        payload,
        "imprest",
        null,
        HRService.uri,
      );

      if (!response?.results) {
        const parsedError = JSON.parse(response?.error);
        Swal.fire("Error", parsedError || "Failed to submit imprest", "error");
        return;
      }

      const imprest = JSON.parse(response.results);
      // 3️⃣ Upload supporting documents
      if (supportingDocuments.length > 0) {
        // double-check sizes before sending
        const maxSize = 5 * 1024 * 1024;
        const invalid = supportingDocuments.filter((f) => f.size > maxSize);
        if (invalid.length > 0) {
          Swal.fire({
            icon: "error",
            title: "File too large",
            text: "One or more documents exceed 5 MB and were not uploaded.",
          });
        }
        const toUpload = supportingDocuments.filter((f) => f.size <= maxSize);
        if (toUpload.length > 0) {
          await uploadSupportingDocuments({
            imprestId: imprest.Id,
            employeeId: resolvedEmployeeId,
            files: toUpload,
          });
        }
      }

      Swal.fire("Success", "Imprest submitted successfully", "success");
      onClose?.();
      onAction?.();
    } catch (error) {
      console.error("Error submitting imprest:", error);
      Swal.fire("Error", error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  /* -----------------------------------------------------------------
     FIELD CHANGE WATCHER
  ----------------------------------------------------------------- */
  const handleFieldChange = (field, value) => {
    if (field.name === "caseRelated") {
      setShowCases(Boolean(value));
    }
  };

  /* -----------------------------------------------------------------
     RENDER
  ----------------------------------------------------------------- */
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DynamicForm
      title={
        isEditing ? "Update Imprest Application" : "Create Imprest Application"
      }
      subtitle="Fill in the imprest details and submit"
      fields={fields}
      steps={steps}
      initialValues={initialValues}
      onFieldChange={handleFieldChange}
      onSubmit={handleSubmit}
    />
  );
};

export default ImprestApplicationForm;
